/**
 * Field types: one Zod schema per kind of value a person can change on a Birdie card, registered with its field kind.
 *
 * Every write contract (quote-write.ts, line-item-write.ts) and every Birdie action input builds its values from these
 * schemas, so the card, Birdie and the platform read one definition. A base type keeps the platform's loosest rule;
 * a contract or an action narrows it (`.trim().min(1).max(40)`), and the kind survives the narrowing.
 *
 * The kinds live in a private registry, never in Zod's global `.meta()`: global metadata reaches `z.toJSONSchema`,
 * which builds the tool schemas the model reads.
 *
 * PURE.
 */
import { z } from "zod";

export const FIELD_KINDS = [
  "text",
  "longText",
  "message",
  "choice",
  "day",
  "money",
  "quantity",
  "unit",
  "lineItems",
  "toggle",
] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

/** The words of one value of a choice: its label, and a sentence under it when the control shows one. */
export interface ChoiceWords {
  readonly label: string;
  readonly hint?: string;
}

export type FieldTypeMeta =
  | { readonly kind: "message"; readonly audience: "customer" | "team" }
  | {
      readonly kind: "choice";
      readonly words: Readonly<Record<string, ChoiceWords>>;
    }
  | { readonly kind: Exclude<FieldKind, "message" | "choice"> };

const fieldTypeRegistry = z.registry<FieldTypeMeta>();

/** Registers `schema` as a field type of `meta.kind` and returns it. */
export function fieldType<S extends z.ZodType>(
  schema: S,
  meta: FieldTypeMeta,
): S {
  // Not schema.register(...): its rest parameter is a conditional type on `this`, which TypeScript cannot resolve for a
  // generic S.
  fieldTypeRegistry.add(schema, meta);
  return schema;
}

/** The wrappers a field keeps its kind through: optional, nullable, a default, and `.required()`'s nonoptional. */
const KIND_KEEPING_WRAPPERS = new Set([
  "optional",
  "nullable",
  "default",
  "nonoptional",
]);

/**
 * The field type of `schema`, or undefined when it has none. Zod's narrowing methods (`.trim()`, `.min()`,
 * `.describe()`, `.refine()`) make a clone whose registry entry is its parent's, so the kind survives them; the four
 * wrappers above are unwrapped. Any other wrapper (`.transform()`, `.pipe()`, a union) has no kind.
 */
export function fieldTypeOf(
  schema: z.core.$ZodType,
): FieldTypeMeta | undefined {
  const own = fieldTypeRegistry.get(schema);
  if (own !== undefined) return own;
  const def = schema._zod.def;
  if (!KIND_KEEPING_WRAPPERS.has(def.type)) return undefined;
  // The four wrappers' defs all carry the wrapped schema as innerType.
  return fieldTypeOf(
    (def as unknown as { innerType: z.core.$ZodType }).innerType,
  );
}

/** Short text: a name, a title. */
export const textSchema = fieldType(z.string(), { kind: "text" });

/** Text that may run to paragraphs: a description, a note. Empty means none. */
export const longTextSchema = fieldType(z.string(), { kind: "longText" });

/** A message the customer reads, exactly as sent. A card refuses one with a [placeholder: …] left in it. */
export const customerMessageSchema = fieldType(z.string(), {
  kind: "message",
  audience: "customer",
});

/** A message only the team reads (a comment on an order). */
export const teamMessageSchema = fieldType(z.string(), {
  kind: "message",
  audience: "team",
});

/** A calendar day, "YYYY-MM-DD", in office time. */
export const calendarDaySchema = fieldType(z.iso.date(), { kind: "day" });

/** Money, in integer cents (never float dollars). */
export const moneyCentsSchema = fieldType(z.number().int(), { kind: "money" });

/** A quantity; decimals allowed. */
export const quantitySchema = fieldType(z.number(), { kind: "quantity" });

/** A line item unit's key ("HOUR", "EACH"), as GET /api/v5/line-item-unit-types lists them. */
export const unitTypeKeySchema = fieldType(z.string(), { kind: "unit" });

/** Yes or no, drawn as a switch: an option recommended, a product the customer supplies. */
export const toggleSchema = fieldType(z.boolean(), { kind: "toggle" });

/** One value of a closed list, with the words every surface shows for it. */
export function choiceSchema<const V extends readonly [string, ...string[]]>(
  values: V,
  words: Readonly<Record<V[number], ChoiceWords>>,
) {
  return fieldType(z.enum(values), { kind: "choice", words });
}
